/**
 * Billing status and subscription management component
 * Per-Use Billing: Task 7.6 - Conditionally hide subscription tiers
 * Observability: Task 4.6 - Integrated with PostHog analytics
 * Refactored: Uses BillingContext for shared state with caching
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Alert, AlertAction, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useBilling } from "@/contexts/useBilling";
import { redirectToCheckout, redirectToPortal } from "@/services/api-client";
import {
  trackCheckoutStarted,
  trackPlanViewed,
  trackPortalOpened,
} from "@/services/analytics";
import { useFeatureFlag } from "@/services/feature-flags";
import type { PlanInterval } from "@/types";
import {
  BillingErrorState,
  BillingHeader,
  BillingSkeleton,
} from "./BillingLayoutParts";
import { PrepaidBillingView } from "./PrepaidBillingView";
import { SubscriptionBillingView } from "./SubscriptionBillingView";
import { useCheckoutReturn } from "../hooks/useCheckoutReturn";

function useBillingActions() {
  const { t } = useTranslation(["billing"]);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);

  const handleSubscribe = async (
    productId: string,
    priceId: string,
    planName?: string,
  ) => {
    setOperationError(null);
    setCheckoutLoading(priceId);
    try {
      await trackCheckoutStarted(planName || priceId);
      await redirectToCheckout({ productId, priceId });
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : (t?.("billing:errors.startCheckout") ?? "Failed to start checkout");
      setOperationError(message);
      setCheckoutLoading(null);
    }
  };

  const handlePurchase = async (purchaseId: string, purchaseName?: string) => {
    setOperationError(null);
    setCheckoutLoading(purchaseId);
    try {
      await trackCheckoutStarted(purchaseName || purchaseId);
      await redirectToCheckout({ purchaseId });
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : (t?.("billing:errors.startCheckout") ?? "Failed to start checkout");
      setOperationError(message);
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setOperationError(null);
    setPortalLoading(true);
    try {
      await trackPortalOpened();
      await redirectToPortal();
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : (t?.("billing:errors.openPortal") ?? "Failed to open portal");
      setOperationError(message);
      setPortalLoading(false);
    }
  };

  return {
    checkoutLoading,
    portalLoading,
    operationError,
    setOperationError,
    handleSubscribe,
    handlePurchase,
    handleManageSubscription,
  };
}

export function BillingStatus() {
  const { t } = useTranslation(["billing"]);

  // Handle the Stripe checkout redirect (?billing=success|cancel): force-refresh
  // stale billing state and toast the outcome.
  useCheckoutReturn();

  const {
    account,
    plans,
    accountLoadingState,
    plansLoadingState,
    accountError,
    plansError,
    refreshAccount,
    refreshPlans,
  } = useBilling();

  // Combined error for display
  const error = accountError || plansError;

  const [selectedInterval, setSelectedInterval] = useState<PlanInterval>(
    "month",
  );

  const {
    checkoutLoading,
    portalLoading,
    operationError,
    setOperationError,
    handleSubscribe,
    handlePurchase,
    handleManageSubscription,
  } = useBillingActions();

  const showSubscriptions = useFeatureFlag("BILLING_SUBSCRIPTIONS");
  const showPrepaid = useFeatureFlag("BILLING_PREPAID");
  const showMetered = useFeatureFlag("BILLING_METERED");
  const isAdminTopUpMode = useFeatureFlag("BILLING_ADMIN_TOP_UP_ONLY");
  // Gated €1 test item: surfaced even under admin-top-up so it can be bought on
  // dev to validate the live Stripe flow, without exposing the subscription tiers.
  const showTestItem = useFeatureFlag("TEST_ITEM_STRIPE");

  useEffect(() => {
    if (plansLoadingState === "success" && plans) {
      void trackPlanViewed();
    }
  }, [plansLoadingState, plans]);

  const loadBillingData = useCallback(async () => {
    // Force: a retry after a failed fetch must bypass the TTL gate, otherwise
    // it stays a no-op for the cache window (30s/5m/10m) and the error panel
    // lingers.
    await Promise.all([refreshAccount(true), refreshPlans(true)]);
  }, [refreshAccount, refreshPlans]);

  const isLoading = accountLoadingState === "loading" ||
    accountLoadingState === "idle" ||
    plansLoadingState === "loading" || plansLoadingState === "idle";

  if (isLoading) {
    return <BillingSkeleton />;
  }

  const hasError = accountLoadingState === "error" ||
    plansLoadingState === "error";
  if (hasError && !isAdminTopUpMode) {
    return <BillingErrorState error={error} onRetry={loadBillingData} />;
  }

  return (
    <section className="space-y-6 rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--card)] p-6 shadow-[var(--sh-xs)]">
      <BillingHeader isAdminTopUpMode={isAdminTopUpMode} />

      {error && !isAdminTopUpMode && (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {operationError && (
        <Alert variant="error">
          <AlertDescription className="flex-1">{operationError}</AlertDescription>
          <AlertAction>
            <Button
              aria-label={t("common:buttons.close")}
              data-test="billing-dismiss-error"
              onClick={() => setOperationError(null)}
              size="icon-sm"
              variant="ghost"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </AlertAction>
        </Alert>
      )}

      <PrepaidBillingView
        isAdminTopUpMode={isAdminTopUpMode}
        showPrepaid={showPrepaid}
        showTestItem={showTestItem}
        plans={plans}
        onPurchase={handlePurchase}
        checkoutLoading={checkoutLoading}
      />

      {/* Subscription surface (status box, tiers, interval toggle, metered
          placeholder) mounts ONLY when subscriptions are enabled, so the
          credit-only prepaid experience never shows it. Reversible: flip
          BILLING_SUBSCRIPTIONS to restore it. */}
      {showSubscriptions && (
        <SubscriptionBillingView
          account={account}
          checkoutLoading={checkoutLoading}
          onManageSubscription={handleManageSubscription}
          onSelectInterval={setSelectedInterval}
          onSubscribe={handleSubscribe}
          plans={plans}
          portalLoading={portalLoading}
          selectedInterval={selectedInterval}
          showMetered={showMetered}
          showSubscriptions={showSubscriptions}
        />
      )}
    </section>
  );
}
